import * as React from 'react';
import { useCallback, useEffect, useRef} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';

/**
 * Piezas del chat "moderno" (composer unificado, textarea que crece,
 * puntitos de typing) adaptadas al Brain.
 *
 * Qué cambió respecto del componente de referencia y por qué:
 *
 * - Va con los TOKENS del tema (bg-card, border, muted-foreground) en vez de
 *   `text-white` / `bg-white/2` hardcodeados: el Brain se usa en claro y
 *   en oscuro, y en claro el original quedaba invisible.
 * - Son piezas sueltas, no una pantalla completa con estado propio: el chat
 *   real ya tiene streaming, conversaciones persistidas, skills, archivos y
 *   citas. Lo que aporta el referente es la CÁSCARA visual, no el estado.
 * - Sin el blob que sigue al mouse ni el <style> inyectado en document.head
 *   al importar (efecto de módulo, se ejecuta aunque nadie lo use).
 * - No redefine `Textarea`: acá ya existe el de shadcn y tener dos con el
 *   mismo nombre es una fuente de bugs.
 */

// ─── Textarea que crece con el contenido ─────────────────────────────────────

export function useAutoResizeTextarea({
  minHeight,
  maxHeight,
}: {
  minHeight: number;
  maxHeight?: number;
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(
    (reset?: boolean) => {
      const el = textareaRef.current;
      if (!el) return;
      if (reset) {
        el.style.height = `${minHeight}px`;
        return;
      }
      el.style.height = `${minHeight}px`;
      el.style.height = `${Math.max(
        minHeight,
        Math.min(el.scrollHeight, maxHeight ?? Number.POSITIVE_INFINITY),
      )}px`;
    },
    [minHeight, maxHeight],
  );

  useEffect(() => {
    const el = textareaRef.current;
    if (el) el.style.height = `${minHeight}px`;
  }, [minHeight]);

  useEffect(() => {
    const onResize = () => adjustHeight();
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [adjustHeight]);

  return { textareaRef, adjustHeight };
}

// ─── Composer: una sola superficie en vez de controles sueltos ───────────────

/**
 * Envuelve el input y sus acciones en una sola tarjeta con foco animado.
 * Los hijos los pone el chat (skills, archivos, enviar): esto es la cáscara.
 */
export function ChatComposer({
  children,
  footer,
  focused = false,
  className,
}: {
  /** El textarea. */
  children: React.ReactNode;
  /** Fila de acciones de abajo (adjuntar, skills, enviar). */
  footer?: React.ReactNode;
  /** Para el anillo de foco (lo maneja el textarea de adentro). */
  focused?: boolean;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25, ease: 'easeOut' }}
      className={cn(
        'relative rounded-2xl border bg-card/80 shadow-xs backdrop-blur-xl',
        'transition-colors duration-200',
        focused ? 'border-primary/40' : 'border-border',
        className,
      )}
    >
      {focused && (
        <motion.span
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-2xl ring-2 ring-primary/20"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        />
      )}
      <div className="relative">{children}</div>
      {footer && (
        <div className="relative flex items-center justify-between gap-3 border-t border-border/60 px-3 py-2">
          {footer}
        </div>
      )}
    </motion.div>
  );
}

// ─── Indicadores ─────────────────────────────────────────────────────────────

/** Tres puntitos que laten mientras el agente piensa. */
export function TypingDots({ className }: { className?: string }) {
  return (
    <span className={cn('inline-flex items-center', className)} aria-hidden>
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="mx-0.5 h-1.5 w-1.5 rounded-full bg-current"
          initial={{ opacity: 0.3 }}
          animate={{ opacity: [0.3, 0.9, 0.3], scale: [0.85, 1.1, 0.85] }}
          transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
        />
      ))}
    </span>
  );
}

/** Entrada escalonada para las sugerencias del estado vacío. */
export function StaggerItem({
  index,
  children,
  className,
}: {
  index: number;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      className={className}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index, 8) * 0.05, duration: 0.3, ease: 'easeOut' }}
    >
      {children}
    </motion.div>
  );
}

/** Título del estado vacío con la línea que se dibuja debajo. */
export function AnimatedHeading({
  title,
  subtitle,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
}) {
  return (
    <div className="space-y-2 text-center">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
        className="inline-block"
      >
        <h2 className="pb-1 text-xl font-semibold tracking-tight">{title}</h2>
        <motion.div
          className="h-px bg-linear-to-r from-transparent via-border to-transparent"
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: '100%', opacity: 1 }}
          transition={{ delay: 0.25, duration: 0.6, ease: 'easeOut' }}
        />
      </motion.div>
      {subtitle && (
        <motion.p
          className="text-sm text-muted-foreground"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
        >
          {subtitle}
        </motion.p>
      )}
    </div>
  );
}

export { AnimatePresence, motion };
